import i18n from "i18next";
import moment from "moment-timezone";
import { VALID_TIMEZONES, DEFAULT_TIMEZONE } from "../../constants/cache";
/**
 * Formatted date according to locale standards.
 */
export const formattedDate = (
  date,
  t,
  todayToString = false,
  todayToEmptyString = false,
  includeYear = false,
) => {
  const today = new Date().toDateString();
  const newDate = new Date(date).toDateString();
  const language = i18n.language || navigator.language;

  if (newDate === today && !todayToString) {
    return todayToEmptyString ? "" : t("Today");
  }

  const dateOptions = {
    month: "short",
    day: "numeric",
  };

  if (includeYear) {
    dateOptions.year = "numeric";
  }

  let formattedString = new Date(date)
    .toLocaleDateString(language, dateOptions)
    .replace(/^\w/, (c) => c.toUpperCase());

  formattedString = formattedString
    .replace(/\bde\b/g, "")
    .replace(/\s+/g, " ")
    .trim();

  return formattedString;
};

/**
 * Formatted time according to french locale standard.
 * Exemple of the expected format:
 */
export const formattedTime = (scanDate) => {
  const language = i18n.language || "fr-FR";
  const options = { hour: "2-digit", minute: "2-digit" };
  const timeString = new Date(scanDate).toLocaleTimeString(language, options);

  // En français, remplacer ":" par "h"
  if (language.startsWith("fr")) {
    return timeString.replace(":", "h");
  }

  return timeString;
};

/** Local capture timestamp for PDF pages: 05/09/2026 15:00:07 */
export const formatLocalCaptureTimestamp = (date) => {
  const value = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(value.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(value.getDate())}/${pad(value.getMonth() + 1)}/${value.getFullYear()} ${pad(value.getHours())}:${pad(value.getMinutes())}:${pad(value.getSeconds())}`;
};

export const deltaMilliseconds = (dateFrom, dateTo) => {
  let from = new Date(dateFrom)?.getTime();
  let to = new Date(dateTo)?.getTime();
  return to - from;
};

export const elapsedTimeContainer = (
  dateMilliseconds,
  language,
  includeAgo,
  dateStopMs = new Date()?.getTime(),
) => {
  if (dateMilliseconds) {
    let currentDateMilliseconds = dateStopMs;
    let scanDateFormatted = new Date(dateMilliseconds)?.getTime();
    let deltaMilliseconds = currentDateMilliseconds - scanDateFormatted;

    let deltaSeconds = Math.floor(deltaMilliseconds / 1000);
    let deltaMinutes = Math.floor(deltaSeconds / 60);
    let deltaHours = Math.floor(deltaMinutes / 60);
    let deltaDays = Math.floor(deltaHours / 24);
    let deltaMonths = Math.floor(deltaDays / 30);

    let sinceDate = "";

    const getTimeLabel = (count, unitKey, includeAgo = true) => {
      if (!count) return `0 ${i18n.t(`units.${unitKey}s`)}`;
      const unit = i18n.t(`units.${unitKey}${count > 1 ? "s" : ""}`);
      return includeAgo
        ? i18n.t("time_ago", { count, unit })
        : i18n.t("time_unit", { count, unit });
    };

    if (deltaMonths >= 1) {
      sinceDate = getTimeLabel(deltaMonths, "month", includeAgo);
    } else if (deltaDays >= 1) {
      sinceDate = getTimeLabel(deltaDays, "day", includeAgo);
    } else if (deltaHours >= 1) {
      sinceDate = getTimeLabel(deltaHours, "hour", includeAgo);
    } else if (deltaMinutes >= 1) {
      sinceDate = getTimeLabel(deltaMinutes, "minute", includeAgo);
    } else {
      sinceDate = getTimeLabel(deltaSeconds, "second", includeAgo);
    }

    return sinceDate;
  }
};

export const formattedDuration = (milliseconds, language) => {
  let deltaSeconds = Math.floor(milliseconds / 1000);
  let deltaMinutes = Math.floor(deltaSeconds / 60);
  let deltaHours = Math.floor(deltaMinutes / 60);
  let deltaDays = Math.floor(deltaHours / 24);

  if (deltaDays >= 1) {
    const remainingHours = deltaHours - deltaDays * 24;
    return i18n.t("time_days_hours", {
      days: deltaDays,
      dayUnit: i18n.t(`units.${deltaDays === 1 ? "day" : "days"}`),
      hours: remainingHours,
      hourUnit: i18n.t(`units.${remainingHours === 1 ? "hour" : "hours"}`),
    });
  } else if (deltaHours >= 1) {
    return i18n.t("time_hours", {
      hours: deltaHours,
      hourUnit: i18n.t(`units.${deltaHours === 1 ? "hour" : "hours"}`),
    });
  } else {
    return i18n.t("time_less_than_hour", {
      hourUnit: i18n.t("units.hour"),
    });
  }
};

export const sinceDate = (dateFrom, dateTo, t) => {
  const from = new Date(dateFrom);
  const to = new Date(dateTo);
  if (isNaN(from) || isNaN(to) || to < from) return t("time.none", "-");

  const deltaMs = to - from;
  const deltaSec = Math.floor(deltaMs / 1000);
  const deltaMin = Math.floor(deltaSec / 60);
  const deltaH = Math.floor(deltaMin / 60);
  const deltaD = Math.floor(deltaH / 24);
  const deltaM = Math.floor(deltaD / 30); // approx
  const deltaY = Math.floor(deltaM / 12); // approx

  const label = (unit, count) => t(`time.${unit}`, { count });

  if (deltaY >= 1) {
    const remM = deltaM - deltaY * 12;
    return remM > 0
      ? `${label("year", deltaY)} ${t("time.and")} ${label("month", remM)}`
      : label("year", deltaY);
  }

  if (deltaM >= 1) {
    const remD = deltaD - deltaM * 30;
    return remD > 0
      ? `${label("month", deltaM)} ${t("time.and")} ${label("day", remD)}`
      : label("month", deltaM);
  }

  if (deltaD >= 1) return label("day", deltaD);
  if (deltaH >= 1) return label("hour", deltaH);
  if (deltaMin >= 1) return label("minute", deltaMin);
  if (deltaSec >= 1) return label("second", deltaSec);

  return t("time.none", "-");
};

export const dateToName = (dateString) => {
  // Format date like 2023_08_10_13T42
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}_${month}_${day}_${hours}T${minutes}`;
};

export const formattedDateDocument = (date) => {
  const jour = String(new Date(date).getDate()).padStart(2, "0");
  const mois = String(new Date(date).getMonth() + 1).padStart(2, "0");
  const annee = new Date(date).getFullYear();

  return `${jour}/${mois}/${annee}`;
};

export const formattedDayName = (date, language) => {
  let locale;

  switch (language) {
    case "en":
      locale = "en-US";
      break;
    case "fr":
      locale = "fr-FR";
      break;
    case "es":
      locale = "es-ES";
      break;
    case "se":
      locale = "se-SE";
      break;
    default:
      locale = "en-US";
  }

  return new Date(date).toLocaleDateString(locale, { weekday: "short" });
};

export const formatDateToString = (date) => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear()).substring(2);
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${day}_${month}_${year}_${hours}h${minutes}min`;
};

export const isMoreThanFiveMinutesAgo = (dateString) => {
  const dateGiven = new Date(dateString);
  const currentDate = new Date();
  const differenceMinutes = (currentDate - dateGiven) / (1000 * 60);
  return differenceMinutes > 5;
};

export const getStartOfWeek = (date) => {
  const day = date.getDay();
  const diff = (day === 0 ? -6 : 1) - day;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + diff);
};

const getEndOfMonth = (date) => {
  const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  endOfMonth.setHours(23, 59, 59, 999);
  return endOfMonth;
};

export const getMonthsAgo = (date, monthsAgo, endOfMonth) => {
  let newDate = new Date(date);
  newDate.setMonth(newDate.getMonth() - (monthsAgo - 1));
  newDate.setDate(1);
  if (endOfMonth) {
    newDate = getEndOfMonth(newDate);
  } else {
    newDate.setHours(0, 0, 0, 0);
  }
  return newDate;
};

export const getWeekNumber = (date, language) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7)); // Se positionner sur le jeudi
  const firstThursday = new Date(d.getFullYear(), 0, 4);
  const weekNumber = Math.ceil(((d - firstThursday) / 86400000 + 1) / 7);
  return `${i18n.t("week_prefix")}-${weekNumber} ${d.getFullYear()}`;
};

export const getMonthLabel = (date) => {
  const locale = i18n.language.includes("-")
    ? i18n.language
    : `${i18n.language}-${i18n.language.toUpperCase()}`;
  return date.toLocaleString(locale, { month: "long", year: "numeric" });
};

export const localeMap = {
  en: "en-US",
  fr: "fr-FR",
  es: "es-ES",
  sv: "sv-SE",
};

export const formattedPredictionDateTime = (
  predictionDatetimeISO,
  timezone = DEFAULT_TIMEZONE,
  t,
) => {
  if (!predictionDatetimeISO || !t) return null;

  try {
    // Validate timezone
    if (!VALID_TIMEZONES.includes(timezone)) {
      console.warn(
        `Timezone invalide : ${timezone}, utilisation de la valeur par défaut`,
      );
      timezone = DEFAULT_TIMEZONE;
    }

    const dateMoment = moment(predictionDatetimeISO).tz(timezone);
    if (!dateMoment.isValid()) return null;

    const dayIndex = dateMoment.day();
    const dayKeys = [
      "Sunday_abbr",
      "Monday_abbr",
      "Tuesday_abbr",
      "Wednesday_abbr",
      "Thursday_abbr",
      "Friday_abbr",
      "Saturday_abbr",
    ];

    const day = t(dayKeys[dayIndex]);
    const dayNum = String(dateMoment.date()).padStart(2, "0");
    const hours = String(dateMoment.hour()).padStart(2, "0");
    const minutes = String(dateMoment.minute()).padStart(2, "0");
    const separator = t("à");

    return `${day} ${dayNum} ${separator} ${hours}:${minutes}`;
  } catch (error) {
    console.error("Error formatting prediction datetime:", error);
    return null;
  }
};
